import { AnimatePresence, motion } from "framer-motion";
import { Camera, Clock, Dumbbell } from "lucide-react";
import { useState } from "react";

import { useTranslation } from "@/core/i18n";

import type { ImagePair } from "./SliderModal";
import { SliderModal } from "./SliderModal";

type RevisitTilesProps = {
  frontFaceImageUrl?: string | null;
  enhancedPhotoUrl?: string | null;
  profileFaceImageUrl?: string | null;
  enhancedProfilePhotoUrl?: string | null;
  futureSelfWithoutTreatmentImageUrl?: string | null;
  futureSelfWithTreatmentImageUrl?: string | null;
  wellnessOriginalImageUrl?: string | null;
  wellnessCombinedImageUrl?: string | null;
};

export function RevisitTiles(props: Readonly<RevisitTilesProps>) {
  const [activeModal, setActiveModal] = useState<ImagePair | null>(null);
  const { t } = useTranslation();

  const tiles: ImagePair[] = [];

  if (props.frontFaceImageUrl && props.enhancedPhotoUrl) {
    const profile =
      props.profileFaceImageUrl && props.enhancedProfilePhotoUrl
        ? {
            beforeUrl: props.profileFaceImageUrl,
            afterUrl: props.enhancedProfilePhotoUrl,
            beforeLabel: t("revisitTiles.before"),
            afterLabel: t("revisitTiles.after"),
          }
        : undefined;

    tiles.push({
      key: "beforeAfter",
      label: t("revisitTiles.beforeAfterLabel"),
      icon: <Camera className="h-5 w-5" />,
      front: {
        beforeUrl: props.frontFaceImageUrl,
        afterUrl: props.enhancedPhotoUrl,
        beforeLabel: t("revisitTiles.before"),
        afterLabel: t("revisitTiles.after"),
      },
      profile,
    });
  }

  if (props.futureSelfWithoutTreatmentImageUrl && props.futureSelfWithTreatmentImageUrl) {
    tiles.push({
      key: "future",
      label: t("revisitTiles.yourFuture"),
      icon: <Clock className="h-5 w-5" />,
      front: {
        beforeUrl: props.futureSelfWithoutTreatmentImageUrl,
        afterUrl: props.futureSelfWithTreatmentImageUrl,
        beforeLabel: t("revisitTiles.withoutCare"),
        afterLabel: t("revisitTiles.withCare"),
      },
    });
  }

  if (props.wellnessOriginalImageUrl && props.wellnessCombinedImageUrl) {
    tiles.push({
      key: "wellness",
      label: t("revisitTiles.wellness"),
      icon: <Dumbbell className="h-5 w-5" />,
      front: {
        beforeUrl: props.wellnessOriginalImageUrl,
        afterUrl: props.wellnessCombinedImageUrl,
        beforeLabel: t("revisitTiles.before"),
        afterLabel: t("revisitTiles.after"),
      },
    });
  }

  if (tiles.length === 0) return null;

  return (
    <>
      <motion.div
        className="flex flex-col gap-3"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <p className="text-xs uppercase tracking-wider text-secondary">
          {t("revisitTiles.heading")}
        </p>
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${Math.min(tiles.length, 3)}, 1fr)` }}
        >
          {tiles.map((tile) => (
            <button
              key={tile.key}
              type="button"
              className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white p-4 transition-colors active:bg-gray-50"
              onClick={() => setActiveModal(tile)}
            >
              <div className="text-secondary">{tile.icon}</div>
              <span className="text-xs font-medium text-primary">{tile.label}</span>
            </button>
          ))}
        </div>
      </motion.div>

      <AnimatePresence>
        {activeModal && <SliderModal pair={activeModal} onClose={() => setActiveModal(null)} />}
      </AnimatePresence>
    </>
  );
}
